/**
 * Internal build and device detection for product analytics.
 *
 * PostHog (client and server forwarding) leaves out anything that is not a
 * real member on a real store build:
 * - QA builds: a native bundle built with any subscription QA flag
 *   (VITE_ENABLE_SUBSCRIPTION_QA_FLAGS / VITE_QA_*), or any development-mode
 *   bundle.
 * - Native builds the store did not ship: debug builds, simulators,
 *   TestFlight (and App Review) installs, and APKs installed by adb or the
 *   package installer. Read once per launch from the native InAppReview
 *   plugin; binaries without that method report unknown (counted).
 * - Internal devices: once an internal test email is seen on a device, the
 *   device stays internal, so later guest sessions on a QA phone (which have
 *   no email) are also left out.
 * - Browser preview deployments and local browser hosts. Host exclusions
 *   never apply to native apps, whose production origin can be localhost.
 */
import { Capacitor } from '@capacitor/core';
import { env } from '@/lib/env';
import {
  getNativeInAppReviewPlugin,
  type NativeInstallChannel,
} from '@/utils/nativeInAppReviewPlugin';

export type InstallChannel = NativeInstallChannel | 'web' | 'unknown';

/** Stable warehouse labels. A null reason means eligible, not verified human. */
export type AnalyticsExclusionReason =
  | 'development_build'
  | 'qa_build'
  | 'preview_web_host'
  | 'local_web_host'
  | 'internal_native_install'
  | 'internal_device';

/** Host exclusions apply only to browsers. Store apps also use localhost. */
export function getWebAnalyticsHostExclusion(
  hostname: string,
  native: boolean
): 'preview_web_host' | 'local_web_host' | null {
  if (native) return null;
  const host = hostname.toLowerCase().replace(/\.$/, '');
  if (host.endsWith('.vercel.app')) return 'preview_web_host';
  if (['localhost', '127.0.0.1', '::1', '[::1]'].includes(host)) return 'local_web_host';
  return null;
}

export interface InstallEnvironment {
  channel: InstallChannel;
  /** True for debug, simulator, TestFlight and sideloaded installs. */
  internal: boolean;
  appVersion: string | null;
  appBuild: string | null;
}

const INTERNAL_ANALYTICS_DEVICE_KEY = 'jp_internal_analytics_device';
const INSTALL_ENVIRONMENT_TIMEOUT_MS = 1000;

let cachedEnvironment: InstallEnvironment | null = null;
let pendingEnvironment: Promise<InstallEnvironment> | null = null;

function isFlagSet(value: string | undefined): boolean {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 && normalized !== 'false';
}

/**
 * True when this native bundle was built for QA (the same flags the release
 * scripts refuse in store builds). Web is judged by account and device only:
 * a QA flag on a web deployment must never switch off web analytics.
 */
export function isQaAnalyticsBuild(): boolean {
  if (import.meta.env.MODE === 'development') return true;
  if (!Capacitor.isNativePlatform()) return false;
  return (
    isFlagSet(env.enableSubscriptionQaFlags) ||
    isFlagSet(env.qaNativeOfferMode) ||
    isFlagSet(env.qaOnboardingOfferMode)
  );
}

async function readInstallEnvironment(timeoutMs: number): Promise<InstallEnvironment> {
  if (!Capacitor.isNativePlatform()) {
    return { channel: 'web', internal: false, appVersion: null, appBuild: null };
  }
  // The native bridge reader loads on first use (native only), keeping it out
  // of the startup bundle. A failed load counts as unknown (see below). The
  // plugin accessor is passed in so its tiny module stays in the main chunk
  // instead of becoming an extra chunk for the session pages (in-app review).
  const { readNativeInstallEnvironment } = await import('@/utils/nativeInstallEnvironment');
  return readNativeInstallEnvironment(timeoutMs, getNativeInAppReviewPlugin);
}

/**
 * Reads the install environment once per launch. Resolves within about a
 * second; failures and older binaries resolve to channel 'unknown'.
 */
export function loadInstallEnvironment(
  timeoutMs = INSTALL_ENVIRONMENT_TIMEOUT_MS
): Promise<InstallEnvironment> {
  if (cachedEnvironment) return Promise.resolve(cachedEnvironment);
  if (!pendingEnvironment) {
    pendingEnvironment = readInstallEnvironment(timeoutMs)
      .catch((): InstallEnvironment => ({
        channel: 'unknown',
        internal: false,
        appVersion: null,
        appBuild: null,
      }))
      .then((environment) => {
        cachedEnvironment = environment;
        pendingEnvironment = null;
        return environment;
      });
  }
  return pendingEnvironment;
}

/** The environment once loaded, else null. */
export function getCachedInstallEnvironment(): InstallEnvironment | null {
  return cachedEnvironment;
}

export function isInternalAnalyticsDevice(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(INTERNAL_ANALYTICS_DEVICE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Remembers that an internal test account used this device. */
export function markInternalAnalyticsDevice(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(INTERNAL_ANALYTICS_DEVICE_KEY, '1');
  } catch {
    // Storage blocked: the email check still covers signed-in sessions.
  }
}

/**
 * Shared reason for QA bundles, preview/local browsers, internal native
 * installs (once loaded), and internal devices. Call loadInstallEnvironment()
 * first on native when the answer must include the install channel.
 */
export function getAnalyticsExclusionReason(): AnalyticsExclusionReason | null {
  if (import.meta.env.MODE === 'development') return 'development_build';
  if (isQaAnalyticsBuild()) return 'qa_build';
  if (cachedEnvironment?.internal === true) return 'internal_native_install';
  if (isInternalAnalyticsDevice()) return 'internal_device';
  if (typeof window !== 'undefined') {
    const hostReason = getWebAnalyticsHostExclusion(window.location.hostname, Capacitor.isNativePlatform());
    // Unit fixtures use jsdom localhost. Production browser localhost remains
    // excluded; native localhost remains eligible in every mode.
    if (hostReason === 'local_web_host' && import.meta.env.MODE === 'test') return null;
    return hostReason;
  }
  return null;
}

export function isInternalAnalyticsBuild(): boolean {
  return getAnalyticsExclusionReason() !== null;
}

/** Test-only: forget the cached environment. */
export function __resetAnalyticsBuildEnvironmentForTests(): void {
  cachedEnvironment = null;
  pendingEnvironment = null;
}
